import type { GameManifestEntry } from '../assets/manifest.js';

/**
 * GameTile — a compact, self-contained game card echoing the Store landing card
 * (art band + gradient + player/time pills + wordmark + breakout character), but
 * phone-sized and dependency-free (no Store CSS classes). Used on the lobby
 * waiting screen so players see the game they're about to play.
 */

const CARD_H = 168;

export function GameTile({
  game,
  actions,
}: {
  game: GameManifestEntry;
  /**
   * Optional white primary pills rendered bottom-right over the art, in a row
   * (was a single `action` — the lobby now offers both "How to play" and
   * "View roles" from the tile).
   */
  actions?: { label: string; onClick: () => void }[];
}) {
  return (
    <div style={{ position: 'relative', width: '100%', paddingTop: 40 }}>
      {/* Art band (clipped) */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: CARD_H,
          borderRadius: 'var(--radius-card)',
          border: '1px solid var(--surface-stroke)',
          overflow: 'hidden',
          background: 'var(--surface-card)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        {game.cardBg && (
          <img
            src={game.cardBg}
            alt=""
            aria-hidden
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}

        {/* Legibility overlay */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.62) 100%)',
          }}
        />

        {/* Player/time pills — top-left */}
        <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 8, zIndex: 2 }}>
          {game.players !== '—' && (
            <span
              style={{
                background: 'var(--pill-info-bg)',
                color: 'var(--pill-info-text)',
                borderRadius: 'var(--radius-pill)',
                padding: '5px 12px',
                fontSize: 12,
                fontFamily: 'var(--font-ui)',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {game.players} players
            </span>
          )}
          {game.time !== '—' && (
            <span
              style={{
                background: 'var(--pill-meta-bg)',
                color: 'var(--pill-meta-text)',
                borderRadius: 'var(--radius-pill)',
                padding: '5px 12px',
                fontSize: 12,
                fontFamily: 'var(--font-ui)',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              {game.time}
            </span>
          )}
        </div>

        {/* Wordmark — bottom-left over the art */}
        <div style={{ position: 'absolute', bottom: 14, left: 16, zIndex: 2, maxWidth: '62%' }}>
          {game.wordmark ? (
            <img
              src={game.wordmark}
              alt={game.name}
              style={{ display: 'block', width: '100%', maxWidth: 210 }}
            />
          ) : (
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 24,
                color: 'var(--text-primary)',
                textShadow: '0 2px 12px rgba(0,0,0,0.8)',
              }}
            >
              {game.name}
            </span>
          )}
        </div>
      </div>

      {/* Character — taller than the card, breaks out above the top edge, right-aligned */}
      {game.character && (
        <img
          src={game.character}
          alt=""
          aria-hidden
          style={{
            position: 'absolute',
            bottom: 1,
            right: 'clamp(6px, 3%, 24px)',
            height: 196,
            objectFit: 'contain',
            objectPosition: 'bottom',
            pointerEvents: 'none',
            zIndex: 3,
            filter: 'drop-shadow(0 10px 26px rgba(0,0,0,0.6))',
          }}
        />
      )}

      {/* Action pills — white primary buttons, bottom-right over the art. They
          wrap rather than run under the wordmark on a narrow tile. */}
      {actions && actions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: 18,
            right: 14,
            zIndex: 4,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            gap: 8,
            maxWidth: 'calc(100% - 28px)',
          }}
        >
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className="rm-hover-pill"
              onClick={action.onClick}
              style={{
                fontFamily: 'var(--font-ui)',
                fontSize: 15,
                fontWeight: 600,
                color: 'var(--btn-primary-text)',
                background: 'var(--btn-primary-bg)',
                border: 'none',
                borderRadius: 'var(--radius-pill)',
                padding: '12px 22px',
                cursor: 'pointer',
                boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
                whiteSpace: 'nowrap',
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
